import { Component } from 'preact';
import { compose } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// Utils
import { actionHandler, changeFocusOnMaxLength, isLoadingOnField,
  stepShowHide, stepHasRequiredData } from '../../../utilities/form/formUtils';
import { setTrygTealium } from '../../../utilities/tracking';

// Components
import Label from '../../Form/Label';
import Description from '../../Form/Description';
import Error from '../../Form/Error';
import { replaceAll } from '../../../utilities/common';

class InputText extends Component {
  constructor(props) {
    super(props);
    this.state = {
      value: Object.hasOwnProperty.call(props.values, props.id) ? props.values[props.id] : '',
    };

    this.handleTextChange = this.handleTextChange.bind(this);
    this.triggerClick = this.triggerClick.bind(this);
  }

  genetiveTextCheck(text) {
    let genetiveText;
    const lastChar = text.substring(text.length, text.length - 1);
    if (lastChar === 's' || lastChar === 'x' || lastChar === 'z') {
      genetiveText = `${text}'`;
    } else {
      genetiveText = `${text}s`;
    }
    return genetiveText;
  }

  updateGenetiveText(id, value, setFieldValue) {
    setFieldValue(`${id}-genetive`, this.genetiveTextCheck(value));
  }

  handleTextChange(event) {
    const {
      onChange,
      onBlur,
      maxLength,
      formValues,
      formValuesTemplate,
      actions,
      dispatch,
      values,
      setValues,
      tealiumMap,
      setFieldValue,
      genetiveCase,
      id,
      actionEnterEnabled,
      updateExtraFields,
    } = this.props;
    const { target: { value } } = event;
    if (tealiumMap) {
      const trackingData = {
        [tealiumMap]: value,
      };
      setTrygTealium(trackingData);
    }

    // Forces onBlur to save data, when value.length is equal to it's maxLength
    if (maxLength && value.length === Number(maxLength)) {
      onBlur(event, formValues, formValuesTemplate);
    }

    this.setState({
      value,
    });
    if (genetiveCase) {
      this.updateGenetiveText(id, value, setFieldValue);
    }
    if (updateExtraFields) {
      updateExtraFields(id, value, setFieldValue);
    }
    // Handle actions on input.
    if (actions.length > 0) {
      actionHandler(actions, onChange, event, dispatch, values, setValues, null,
        actionEnterEnabled);
    } else {
      onChange(event);
    }
  }

  triggerClick = (e) => {
    const {
      onChange,
      actions,
      dispatch,
      values,
      setValues,
    } = this.props;
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur();
      actionHandler(actions, onChange, e, dispatch, values, setValues);
    }
  }

  componentDidMount(){
    const {
      autoFocus = false
    } = this.props;
    if(autoFocus) {
      this.nameInput.focus();
    }
  }

  render() {
    const {
      id,
      desc,
      label,
      placeholder,
      type,
      onBlur,
      errors,
      touched,
      required,
      columns,
      mobileColumns,
      maxLength,
      endFocus,
      formValues,
      formValuesTemplate,
      loadingOnField,
      translations,
      userData,
      value,
      values,
      variant,
      labelActions,
      dispatch,
      setValues,
      requiredData,
      autoComplete,
      actionVariant,
      labelClass,
      updateValue,
      showHide,
      hidden,
    } = this.props;

    let newValue = this.state.value;
    if (value) {
      newValue = updateValue(this.state.value, values);
    }
    const isLoading = loadingOnField ? isLoadingOnField(loadingOnField, userData) : null;

    const formFieldClass = classNames(
      'form__field',
      'form__field--textfield', {
        'form__field--no-label': !label,
        [`form__field--${id}`]: id,
        [`form__field--${columns}col`]: columns,
        [`form__field--mobile-${mobileColumns}col`]: mobileColumns,
      },
      isLoading
    );

    const inputFieldClass = classNames(
      'form__item', {
        'form__item--no-label': !label,
      }
    );

    const labelProps = {
      actions: labelActions,
      dispatch,
      values,
      setValues,
    };

    const onTextBlur = (event) => {
      // Pass props needed for on blur event.
      onBlur(event, formValues, formValuesTemplate);
    };
    if (stepShowHide(hidden, showHide, userData, values)) {
      return <div />;
    }

    const isDisabled = !stepHasRequiredData(requiredData, userData, values);

    return (
      <div className={formFieldClass}>

        {/* Label */}
        {label &&
          <Label
            id={id}
            label={label}
            className={labelClass}
            {...labelProps}
          />
        }
        {actionVariant &&
          <input
            className={inputFieldClass}
            type={variant || type}
            id={id}
            name={id}
            placeholder={replaceAll(placeholder, translations, values)}
            onChange={this.handleTextChange}
            onInput={event => changeFocusOnMaxLength(
              event,
              endFocus,
              maxLength,
              this.handleTextChange
            )}
            onKeyPress={e => this.triggerClick(e) }
            onBlur={onTextBlur}
            required={required}
            maxLength={maxLength}
            endFocus={endFocus}
            value={actionVariant ? newValue * 12 : newValue}
            disabled={isDisabled}
            ref={(input) => { this.nameInput = input; }}
            tabIndex={0}
          />
        }
        {/* Input Field */}
        <input
          className="form__item"
          type={variant || type}
          id={id}
          name={id}
          placeholder={replaceAll(placeholder, translations, values)}
          onChange={this.handleTextChange}
          onInput={event => changeFocusOnMaxLength(
            event,
            endFocus,
            maxLength,
            this.handleTextChange
          )}
          onKeyPress={e => this.triggerClick(e) }
          onBlur={onTextBlur}
          required={required}
          maxLength={maxLength}
          endFocus={endFocus}
          value={newValue}
          disabled={isDisabled}
          autoComplete={autoComplete}
          ref={(input) => { this.nameInput = input; }}
          tabIndex={0}
        />
        {/* Error Message */}
        <Error touched={touched} errors={errors} id={id}/>

        {/* Description */}
        {desc &&
          <Description desc={desc}/>
        }
        {actionVariant === 'confirmationTitle' &&
          <Description id={id} desc={desc}/>
        }
      </div>
    );
  }
}

export default compose(
  connect((state) => {
    const {
      section: {
        packageData: {
          translations,
        },
      },
    } = state;

    return {
      translations,
    };
  })
)(InputText);

InputText.defaultProps = {
  required: false,
  placeholder: null,
  desc: null,
  label: null,
  maxLength: null,
  endFocus: null,
  mobileColumns: null,
  formValues: [],
  formValuesTemplate: '',
  actions: [],
  tealiumMap: null,
  loadingOnField: '',
  value: null,
  genetiveCase: false,
  variant: '',
  labelActions: null,
  requiredData: null,
  autoComplete: 'on',
  autoFocus: false,
  actionVariant: '',
  actionEnterEnabled: true,
  labelClass: '',
  updateExtraFields: null,
  showHide: null,
  hidden: false,
};

InputText.propTypes = {
  id: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  formValues: PropTypes.array,
  formValuesTemplate: PropTypes.string,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  columns: PropTypes.string.isRequired,
  mobileColumns: PropTypes.string,
  label: PropTypes.string,
  desc: PropTypes.string,
  maxLength: PropTypes.number,
  endFocus: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  labelActions: PropTypes.object,
  values: PropTypes.object.isRequired,
  actions: PropTypes.array,
  dispatch: PropTypes.func.isRequired,
  setValues: PropTypes.func.isRequired,
  tealiumMap: PropTypes.string,
  translations: PropTypes.object.isRequired,
  loadingOnField: PropTypes.string,
  userData: PropTypes.object.isRequired,
  value: PropTypes.string,
  genetiveCase: PropTypes.bool,
  requiredData: PropTypes.object,
  setFieldValue: PropTypes.func.isRequired,
  variant: PropTypes.string,
  autoComplete: PropTypes.string,
  autoFocus: PropTypes.bool,
  actionVariant: PropTypes.string,
  actionEnterEnabled: PropTypes.bool,
  labelClass: PropTypes.string,
  updateValue: PropTypes.func.isRequired,
  updateExtraFields: PropTypes.func,
  showHide: PropTypes.object,
  hidden: PropTypes.bool,
};
